import React from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import { Button, CreateReleaseButton } from "ui-kit/Button";
import { StepMore } from "./types";
import GeneralModal1 from "../../assets/general-modal1.webp";
import GeneralModal2 from "../../assets/general-modal2.webp";
import GeneralModal3 from "../../assets/general-modal3.webp";
import { DeviceSize } from "constants/deviceSize";

const getTitle = (step: StepMore | null) => {
  if (!step) return "";

  switch (step) {
    case StepMore.Upload:
      return <FormattedMessage id="Welcome.Modal.Upload.Title" defaultMessage="Upload your music and submit it for moderation" />;
    case StepMore.Create:
      return <FormattedMessage id="Welcome.Modal.Create.Title" defaultMessage="Create a multi-link and track your stats" />;
    case StepMore.Receive:
      return <FormattedMessage id="Welcome.Modal.Receive.Title" defaultMessage="Receive quarterly reports, royalties, and withdraw earnings" />;
    default:
      return "";
  }
};

const getSubtitle = (step: StepMore | null) => {
  if (!step) return "";

  switch (step) {
    case StepMore.Upload:
      return <FormattedMessage id="Welcome.Modal.Upload.Subtitle" defaultMessage="Upload your music, cover art, and complete metadata like track title, artist name, and contributor details. Choose your release date and platforms. Submit for review, and once approved by our moderation team, your music will be distributed to the selected platforms" />;
    case StepMore.Create:
      return <FormattedMessage id="Welcome.Modal.Create.Subtitle" defaultMessage="Once your release is live, we’ll create a multi-link to help you promote your music on social media and share it with fans. Use your dashboard to track streams, audience demographics, and analytics to monitor your music's performance" />;
    case StepMore.Receive:
      return <FormattedMessage id="Welcome.Modal.Receive.Subtitle" defaultMessage="Receive quarterly royalty reports and withdraw your earnings directly to your preferred payment method" />;
    default:
      return "";
  }
};

interface InfoModalProps {
  isOpen: boolean;
  openStep: StepMore | null;
  onHide: () => void;
  onNext: () => void;
  onCreateRelease: () => void;
  onStepChange?: (step: StepMore) => void;
}

export const InfoModal: React.FC<InfoModalProps> = props => {
  const { isOpen, openStep, onHide, onNext, onCreateRelease, onStepChange } = props;
  if (!isOpen) return null; // simple modal toggle

  const bgImage =
    openStep === StepMore.Upload
      ? GeneralModal1
      : openStep === StepMore.Create
        ? GeneralModal2
        : GeneralModal3;

  return (
    <Overlay>
      <ModalContainer
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      >
        <CloseButton onClick={onHide}>✕</CloseButton>

        <ModalBody>
          <StyledTitle variant="title1"  style={{width: (openStep !== StepMore.Create && openStep !== StepMore.Upload) ? '70%' : '100%'}}>{getTitle(openStep)}</StyledTitle>
          <StyledSubtitle variant="body1" style={{width: (openStep !== StepMore.Create && openStep !== StepMore.Upload) ? '65%' : openStep === StepMore.Create ?"80%" :'80%'}}>{getSubtitle(openStep)}</StyledSubtitle>

        </ModalBody>

        <ModalFooter>
          <Dots>
            <Dot
              type="button"
              isSelected={openStep === StepMore.Upload}
              onClick={() => onStepChange?.(StepMore.Upload)}
              aria-label="Upload step"
            />
            <Dot
              type="button"
              isSelected={openStep === StepMore.Create}
              onClick={() => onStepChange?.(StepMore.Create)}
              aria-label="Create step"
            />
            <Dot
              type="button"
              isSelected={openStep === StepMore.Receive}
              onClick={() => onStepChange?.(StepMore.Receive)}
              aria-label="Receive step"
            />
          </Dots>

          <ModalFooterButtons>
            {openStep !== StepMore.Receive && (
              <NextButton onClick={onNext}>
                <FormattedMessage id="Welcome.Modal.NextButton" defaultMessage="Next" />
              </NextButton>
            )}
            <CreateReleaseButton onClick={onCreateRelease}>
              <FormattedMessage id="Welcome.CreateRelease" defaultMessage="Create a release" />
            </CreateReleaseButton>
          </ModalFooterButtons>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
};


const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    padding: 0 8px;
  }
`;

const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 464px;
  width: 829px;
  border-radius: 40px;
  background: rgba(245, 246, 255, 1);
  
  padding: 40px;
  background-repeat: no-repeat;
  background-position: calc(100% + 25vw) center;
  background-size: cover;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 100%;
    max-width: 480px;
    height: auto;
    min-height: 60vh;
    border-radius: 24px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -21px;
  right: -41px;
  z-index: ;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  font-size: 18px;
  cursor: pointer;
  background: none;
  border:none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    top: 12px;
    right: 12px;
    background: rgba(15, 23, 42, 0.75);
    color: #fff;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 25px;
`;

const StyledTitle = styled.div<{ variant?: string }>`
  text-align: left;
  font-size: 30px;
  line-height: 42px;
  font-family: Outfit;
  font-weight: 500;
`;

const StyledSubtitle = styled.div<{ variant?: string }>`
  text-align: left;
  font-family: SF Pro Display;
  font-weight: 400;
  font-size: 18px;
  line-height: 32px;
`;


const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
`;

const Dot = styled.button<{ isSelected?: boolean }>`
  height: 12px;
  width: 12px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.palette.border.accent};
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.palette.background.accent : "transparent"};
  padding: 0;
  cursor: pointer;
  outline: none;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background-color 0.12s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 0 4px rgba(103, 119, 255, 0.18);
  }

  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(103, 119, 255, 0.4);
  }
`;

const ModalFooterButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const NextButton = styled.div`
  cursor: pointer;
  text-decoration: underline;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    display: none;
  }
`;
